/**
 * useReferralCapture — captura o `?ref=` da URL e aplica assim que existir
 * sessão.
 *
 * Montado uma vez no topo da árvore de rotas. Duas fases separadas de
 * propósito: a captura roda mesmo deslogado (o visitante cai na landing pelo
 * link do creator), e a aplicação espera `uid` + onboarding concluído — o
 * doc de `users/{uid}` só existe depois disso, e a callable exige perfil.
 */

import { useEffect, useRef } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { redeemCreatorCode } from "../../../services/firebase/creatorService";
import {
  captureReferralFromUrl,
  clearPendingReferral,
  readPendingReferral,
} from "../lib/referralCapture";

/**
 * Erros em que insistir não adianta — o código não vale ou o usuário já tem
 * atribuição. Qualquer outro (rede, indisponível, cap diário) mantém o
 * código guardado pra tentar na próxima sessão.
 */
const TERMINAL_ERROR_CODES = new Set([
  "functions/not-found",
  "functions/invalid-argument",
  "functions/failed-precondition",
  "functions/already-exists",
  "functions/permission-denied",
]);

export function useReferralCapture(): void {
  const { user, profile } = useAuth();
  const appliedRef = useRef(false);

  // Fase 1 — captura, independente de sessão.
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // Fase 2 — aplicação.
  useEffect(() => {
    if (appliedRef.current) return;
    if (!user?.uid || !profile?.onboardingCompleted) return;

    // Já tem creator atribuído: o servidor devolveria `already_referred` de
    // qualquer jeito. Poupa a chamada e libera o slot do rate-limit.
    if (profile.referredBy) {
      clearPendingReferral();
      return;
    }

    const code = readPendingReferral();
    if (!code) return;

    // Trava síncrona: o efeito re-dispara a cada mudança de profile e a
    // callable tem cap diário de tentativas.
    appliedRef.current = true;

    void redeemCreatorCode(code)
      .then(() => {
        clearPendingReferral();
      })
      .catch((err: unknown) => {
        const code = (err as { code?: string })?.code ?? "";
        if (TERMINAL_ERROR_CODES.has(code)) {
          clearPendingReferral();
          return;
        }
        // Transitório: devolve a trava pra tentar de novo na próxima sessão.
        appliedRef.current = false;
        console.debug("[Lura] aplicação de referral adiada", err);
      });
  }, [user?.uid, profile?.onboardingCompleted, profile?.referredBy]);
}
